import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { RouteGuard } from "@/components/auth/RouteGuard";
import { PermissionDenied } from "@/components/auth/PermissionDenied";
import { PageLoader } from "@/components/ui/loader";
import {
  canManageUsers,
  canManageMasters,
  canManageAssignmentRules,
} from "@/lib/permissions";
import { BranchesTab } from "@/components/admin/BranchesTab";
import { DepartmentsTab } from "@/components/admin/DepartmentsTab";
import { LocationsTab } from "@/components/admin/LocationsTab";
import { StoresTab } from "@/components/admin/StoresTab";
import { AssetCategoriesTab } from "@/components/admin/AssetCategoriesTab";
import { VendorsTab } from "@/components/admin/VendorsTab";
import { ChecklistsTab } from "@/components/admin/ChecklistsTab";
import { DynamicFieldsTab } from "@/components/admin/DynamicFieldsTab";
import { AssignmentRulesTab } from "@/components/admin/AssignmentRulesTab";
import { UsersTab } from "@/components/admin/UsersTab";
import { RolesTab } from "@/components/admin/RolesTab";
import { RolePermissionMatrixTab } from "@/features/rbac/RolePermissionMatrixTab";
import { TemporaryAccessTab } from "@/features/rbac/TemporaryAccessTab";

const adminSearchSchema = z.object({ tab: z.string().optional() });

export const Route = createFileRoute("/_authenticated/admin")({
  validateSearch: adminSearchSchema,
  component: () => (
    <RouteGuard module="admin_assignment_rules">
      <AdminPage />
    </RouteGuard>
  ),
});

function AdminPage() {
  const { data } = useCurrentUser();
  const navigate = useNavigate();
  const search = useSearch({ from: "/_authenticated/admin" });
  const roles = data?.roles ?? [];

  const showUsers = canManageUsers(roles);
  const showMasters = canManageMasters(roles);
  const showAssignment = canManageAssignmentRules(roles);

  const defaultTab = useMemo(() => {
    if (showUsers) return "users";
    if (showAssignment) return "assignment-rules";
    return "users";
  }, [showUsers, showAssignment]);

  const [tab, setTab] = useState<string>(search.tab ?? defaultTab);

  useEffect(() => {
    if (search.tab && search.tab !== tab) setTab(search.tab);
  }, [search.tab]); // eslint-disable-line react-hooks/exhaustive-deps

  // If a non-super-admin lands on a restricted tab, fall back.
  useEffect(() => {
    if (!data) return;
    const restricted: Record<string, boolean> = {
      users: !showUsers,
      roles: !showUsers,
      "role-permissions": !showUsers,
      "temporary-access": !showUsers,
      branches: !showMasters,
      departments: !showMasters,
      locations: !showMasters,
      "asset-categories": !showMasters,
      vendors: !showMasters,
      checklists: !showMasters,
      "dynamic-fields": !showMasters,
      "assignment-rules": !showAssignment,
    };
    if (restricted[tab]) {
      setTab(defaultTab);
      navigate({ to: "/admin", search: { tab: defaultTab } });
    }
  }, [tab, data, showUsers, showMasters, showAssignment, defaultTab, navigate]);

  if (!data) return <PageLoader />;
  if (!showUsers && !showMasters && !showAssignment) {
    return <PermissionDenied />;
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Admin</h1>
        <p className="text-sm text-muted-foreground">
          {showUsers
            ? "Manage users, organisation, location hierarchy, asset master, vendors and checklists."
            : "Manage work order assignment rules for your scope."}
        </p>
      </div>
      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="flex flex-wrap h-auto">
          {showUsers && <TabsTrigger value="users">Users &amp; Roles</TabsTrigger>}
          {showUsers && <TabsTrigger value="roles">Roles</TabsTrigger>}
          {showUsers && <TabsTrigger value="role-permissions">Role Permissions</TabsTrigger>}
          {showUsers && <TabsTrigger value="temporary-access">Temporary Access</TabsTrigger>}
          {showMasters && <TabsTrigger value="branches">Branches</TabsTrigger>}
          {showMasters && <TabsTrigger value="departments">Departments</TabsTrigger>}
          {showMasters && <TabsTrigger value="locations">Locations</TabsTrigger>}
          {showMasters && <TabsTrigger value="stores">Stores</TabsTrigger>}
          {showMasters && <TabsTrigger value="asset-categories">Asset Master</TabsTrigger>}
          {showMasters && <TabsTrigger value="vendors">Vendors</TabsTrigger>}
          {showMasters && <TabsTrigger value="checklists">Checklists</TabsTrigger>}
          {/* {showMasters && <TabsTrigger value="dynamic-fields">Dynamic Fields</TabsTrigger>} */}
          {showAssignment && <TabsTrigger value="assignment-rules">WO Assignment</TabsTrigger>}
        </TabsList>
        {showUsers && <TabsContent value="users"><UsersTab /></TabsContent>}
        {showUsers && <TabsContent value="roles"><RolesTab /></TabsContent>}
        {showUsers && <TabsContent value="role-permissions"><RolePermissionMatrixTab /></TabsContent>}
        {showUsers && <TabsContent value="temporary-access"><TemporaryAccessTab /></TabsContent>}
        {showMasters && <TabsContent value="branches"><BranchesTab /></TabsContent>}
        {showMasters && <TabsContent value="departments"><DepartmentsTab /></TabsContent>}
        {showMasters && <TabsContent value="locations"><LocationsTab /></TabsContent>}
        {showMasters && <TabsContent value="stores"><StoresTab /></TabsContent>}
        {showMasters && <TabsContent value="asset-categories"><AssetCategoriesTab /></TabsContent>}
        {showMasters && <TabsContent value="vendors"><VendorsTab /></TabsContent>}
        {showMasters && <TabsContent value="checklists"><ChecklistsTab /></TabsContent>}
        {/* {showMasters && <TabsContent value="dynamic-fields"><DynamicFieldsTab /></TabsContent>} */}
        {showAssignment && <TabsContent value="assignment-rules"><AssignmentRulesTab /></TabsContent>}
      </Tabs>
    </div>
  );
}
